import { useCallback, useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import { CashSessionError, validateMovement, type CashSessionStatus } from '@lensmira/domain'

import { formatWireForDisplay } from '../money.js'
import type { WireMoney } from '../money.js'
import { toCashCountResult, WriteResultMismatch } from '../api/write-results.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { Ltr, useTranslation } from '../i18n.js'
import { sentence } from '../sentence.js'
import { cashSessionStatusLabel } from '../status-labels.js'
import { connectionLine, writeLine } from '../queue/status-line.js'
import { useWriteQueue } from '../queue/use-write-queue.js'

/**
 * The cash drawer. §6.7 `POS-004`; Appendix C.7.
 *
 * Three writes, all through the queue (`OFF-001a`): open with a float, record a pay-in or pay-out
 * with a reason, and count and close.
 *
 * **The blind count is the requirement, and it is a UI rule as much as a server one.** The counting
 * panel below never renders a computed figure, not the opening float's running total, not a
 * preview, not a placeholder that fills in once known. `packages/domain/src/cash-session.ts` says
 * it plainly: "there is no endpoint that will tell a till what it ought to hold", and this screen
 * does not invent one out of the pieces it already has on hand, which it could: the opening float
 * and the movement list are both in `props` before a count is ever taken. Adding them up here would
 * put the exact figure `reconcile()` computes on screen anyway, through a side door.
 *
 * The result (expected, variance, the Z-report) is a **prop**, `countResult`, supplied by the
 * parent once the server has answered. It is never computed in this file, and its presence alone
 * is not enough to show it, either: the panel also checks `revealed`, set only when this render
 * submitted a count, so a parent that passes a stale result from an earlier session on first mount
 * cannot make it appear before anyone here has counted anything. See `integrationNotes` for why the
 * result has to arrive this way rather than from the write itself.
 *
 * A variance never offers its own approval. `approveVariance` refuses the person who counted with a
 * 403 (`SELF_APPROVAL_REFUSED`), and a button here that only works for someone else is the control
 * Appendix C.7's fraud table calls out: it costs one click and stops nothing. This screen states
 * that a manager who did not count the drawer must approve it, and leaves approving to whatever
 * screen that manager uses.
 */

/** `../money.js`'s wire shape, under the name this screen's props have always used. */
export type MoneyView = WireMoney

/**
 * **How many pay-ins and pay-outs, never how much.**
 *
 * This screen used to take a `movements` array of amounts, and nothing could ever fill it:
 * `cash-session-read.service.ts` answers an uncounted session with `movementCount` and no amount
 * anywhere, because `POS-004`'s blind count means the till must not be able to reconstruct what the
 * drawer should hold, and the movements are one of the three terms that would. So the panel showed
 * "The pay-in saved." directly above "No pay-ins or pay-outs recorded yet.", both true as written.
 *
 * The count is what the read supplies and what a person at the till actually needs: whether the
 * thing they just recorded is there. The amounts arrive with the Z-report, after the count, which is
 * the only moment `POS-004` allows them.
 */

export interface CashSessionSummary {
  readonly id: string
  readonly status: CashSessionStatus
  readonly terminalId: string
  /**
   * Who counted the drawer, once somebody has. `cash-session-read.service.ts` has always sent it
   * and the mapper dropped it.
   *
   * It is what lets this screen offer the approval to the one person who can use it.
   * `approveVariance` refuses the counter with `SELF_APPROVAL_REFUSED`, which is a 403, and a 403
   * is permanent: offering the button to everybody would put a refused write at the head of a
   * serial queue and stop the till a second way.
   */
  readonly countedByStaffId?: string
  /**
   * **Absent while the session is still open, and that is the server-s choice, not an oversight.**
   *
   * `GET /cash-sessions/current` withholds the float until a count exists, taking `POS-004`-s blind
   * count strictly: the float plus the takings a person at the till can see for themselves is close
   * enough to the expected total to spoil one. So this is optional, and the screen shows it only
   * when the server sent it.
   *
   * It was declared required, and the screen rendered it for any non-closed session. The types
   * agreed because the read crosses the wire as JSON and nothing validates it, so a required field
   * the server never sends typechecked perfectly and crashed the screen the moment a drawer was
   * actually open. Found by opening a drawer in a browser, not by any of the 207 tests.
   */
  readonly openingFloat?: MoneyView
  /** Pay-ins and pay-outs recorded so far. A count, deliberately, not the amounts. */
  readonly movementCount?: number
}

export interface ZReportMovementView {
  readonly amount: MoneyView
  readonly reason: string
}

export interface ZReportView {
  readonly openingFloat: MoneyView
  readonly cashTaken: MoneyView
  readonly payIns: MoneyView
  readonly payOuts: MoneyView
  readonly movements: readonly ZReportMovementView[]
}

/** The server's answer to a submitted count. Never built here, only ever passed in. */
export interface CashCountResult {
  readonly status: CashSessionStatus
  readonly expected: MoneyView
  readonly counted: MoneyView
  readonly variance: MoneyView
  readonly balanced: boolean
  readonly zReport: ZReportView
}

export interface CashSessionProps {
  readonly branchId: string
  readonly terminalId: string
  /** The session this screen is working against. Absent (or `closed`) shows the open panel. */
  readonly session?: CashSessionSummary
  /**
   * This screen's own `POST /cash-sessions/count` answer, when a parent has one to supply.
   *
   * Kept as a prop although the screen now reads its own write's response out of the queue, because
   * a parent that has the session from a read (a variance approved by somebody else, say) is a
   * legitimate second source. The screen's own write wins where both exist: it is the answer to the
   * count that was just taken here.
   */
  readonly countResult?: CashCountResult
  /**
   * Called once the **server** has taken each write, so the parent can re-read.
   *
   * Not at enqueue time. `enqueue` resolves when the write is persisted, before the transport runs,
   * so a parent re-reading then gets the state from before the write: opening a drawer left the
   * shell's banner reading "Drawer closed" and `TakePayment` refusing cash into the session that had
   * just opened. See `use-settled-write.ts`.
   */
  readonly onSaved?: () => void
  /**
   * `cash_variance_approve`, Appendix B's owner and manager, and who this reader is.
   *
   * Both, because the row alone is not enough: `approveVariance` refuses the person who counted
   * with `SELF_APPROVAL_REFUSED`, a 403, and a 403 is permanent, so a manager who counted their own
   * drawer and tapped Approve would put a refused write at the head of a serial queue and stop the
   * till a second way. The control is offered when the reader holds the row **and** did not count.
   */
  readonly canApproveVariance?: boolean
  readonly staffId?: string
}

/**
 * Minor units to what a shopkeeper reads, with the currency code in front. `../money.js`'s
 * `formatMoney` does the actual digit work (bigint arithmetic throughout, decimal places by
 * currency), shared by every screen so no two of them can disagree about how an amount is shown;
 * this only adapts it to the `{ minor, currency }` wire shape everything in this file already
 * carries. `MR-01`.
 */
function formatMoney(value: MoneyView): string {
  return formatWireForDisplay(value.minor, value.currency)
}

/**
 * What a shopkeeper types, as minor units. Never a typed minus: a pay-out is a direction choice
 * that this screen negates itself, not a sign the till has to trust.
 */
function toMinorUnits(input: string): bigint | null {
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(input.trim())
  if (!match) return null
  const whole = match[1] as string
  const fraction = (match[2] ?? '').padEnd(2, '0')
  return BigInt(whole) * 100n + BigInt(fraction)
}

export function CashSession({
  branchId,
  terminalId,
  session,
  countResult,
  onSaved,
  canApproveVariance,
  staffId,
}: CashSessionProps): JSX.Element {
  const { enqueue, snapshot } = useWriteQueue()
  const { t } = useTranslation()

  const [floatInput, setFloatInput] = useState('')
  const [floatError, setFloatError] = useState<string | null>(null)
  const [openWriteId, setOpenWriteId] = useState<string | null>(null)

  const [direction, setDirection] = useState<'in' | 'out'>('in')
  const [amountInput, setAmountInput] = useState('')
  const [reason, setReason] = useState('')
  const [movementError, setMovementError] = useState<string | null>(null)
  const [movementWriteId, setMovementWriteId] = useState<string | null>(null)

  const [countInput, setCountInput] = useState('')
  const [countError, setCountError] = useState<string | null>(null)
  const [countWriteId, setCountWriteId] = useState<string | null>(null)
  const [approvalNote, setApprovalNote] = useState('')
  const [approvalError, setApprovalError] = useState<string | null>(null)
  const [approveWriteId, setApproveWriteId] = useState<string | null>(null)
  /**
   * Set only by this screen submitting a count, in this render. `countResult` alone is not
   * sufficient to show the reconciliation panel: a parent could pass one from an earlier session on
   * first mount, and rendering it then would be the expected figure appearing before anyone here
   * had counted, which is the one thing a blind count cannot do.
   */
  const [revealed, setRevealed] = useState(false)

  /**
   * The reveal, read off this screen's own count write once the server has answered it.
   *
   * `POS-004`'s blind count is "count without seeing anything, then the expected figure and the
   * variance appear". The server computed both and nothing ever showed them: `countResult` was a
   * prop and no caller passed one, so a till counted 7,500.00 against an expected 7,500.00 and was
   * never told either figure or that the drawer had closed.
   *
   * Looked up by `countWriteId`, the id this render stored, and not by "the latest count write in
   * the queue". The distinction is the same one `revealed` exists for: a result belonging to an
   * earlier session must not be able to appear here.
   */
  const [countedHere, setCountedHere] = useState<CashCountResult | undefined>(undefined)
  const [resultError, setResultError] = useState<string | null>(null)

  useWriteSettled(snapshot, countWriteId, (write) => {
    try {
      setCountedHere(toCashCountResult(write.response))
    } catch (error) {
      // The count itself was accepted; it is the answer that could not be read. Said in those words
      // rather than silently showing nothing, because a till that counted and saw no variance would
      // reasonably believe the drawer balanced.
      setResultError(
        error instanceof WriteResultMismatch ? error.message : t('cashSession.resultUnreadable'),
      )
    }
    onSaved?.()
  })

  useWriteSettled(snapshot, openWriteId, () => {
    onSaved?.()
  })

  useWriteSettled(snapshot, movementWriteId, () => {
    onSaved?.()
  })

  /**
   * A **different** session starts the count step over. A session going away does not.
   *
   * The distinction is not pedantic: counting closes the drawer, so the parent's next read answers
   * with no session at all, and a reset keyed on `session?.id` changing therefore fired the instant
   * the count succeeded and wiped the reveal it had just produced. The Z-report appeared and
   * vanished, which is worse than never showing it, because the till has no way to ask for it back.
   * Caught by the `App`-level test, and not by this screen's own suite, where nothing re-reads.
   */
  const countedFor = useRef<string | undefined>(session?.id)

  useEffect(() => {
    if (session?.id === undefined) return
    if (session.id === countedFor.current) return
    countedFor.current = session.id
    setCountInput('')
    setCountError(null)
    setCountWriteId(null)
    setCountedHere(undefined)
    setResultError(null)
    setRevealed(false)
  }, [session?.id])

  const openDrawer = useCallback(async () => {
    const minor = toMinorUnits(floatInput)
    if (minor === null || minor < 0n) {
      setFloatError(t('cashSession.floatNegative'))
      return
    }
    setFloatError(null)
    const write = (await enqueue({
      method: 'POST',
      path: '/cash-sessions',
      label: t('cashSession.queue.openLabel', { terminalId }),
      body: { branchId, terminalId, openingFloatMinor: minor.toString() },
    })) as { id: string }
    setOpenWriteId(write.id)
  }, [branchId, terminalId, floatInput, enqueue, t])

  const recordMovement = useCallback(async () => {
    if (!session) return
    const minor = toMinorUnits(amountInput)
    if (minor === null) {
      setMovementError(t('cashSession.amountRequired'))
      return
    }
    const signed = direction === 'out' ? -minor : minor
    try {
      validateMovement({ amountMinor: signed, reason: reason.trim() })
    } catch (error) {
      setMovementError(
        error instanceof CashSessionError ? error.message : t('cashSession.movementRefused'),
      )
      return
    }
    setMovementError(null)
    const write = (await enqueue({
      method: 'POST',
      path: '/cash-sessions/movements',
      label: t(
        direction === 'out' ? 'cashSession.queue.payOutLabel' : 'cashSession.queue.payInLabel',
        { terminalId: session.terminalId },
      ),
      body: { sessionId: session.id, amountMinor: signed.toString(), reason: reason.trim() },
    })) as { id: string }
    setMovementWriteId(write.id)
    setAmountInput('')
    setReason('')
  }, [session, amountInput, reason, direction, enqueue, t])

  const approveVariance = useCallback(async () => {
    if (!session) return
    if (approvalNote.trim().length < 3) {
      // `requireNote`'s own test, to the character. Without it "the variance report records that a
      // difference existed and nothing about it", which is the server's own sentence.
      setApprovalError(t('cashSession.approveNoteRequired'))
      return
    }
    setApprovalError(null)
    const write = (await enqueue({
      method: 'POST',
      path: '/cash-sessions/approve',
      label: t('cashSession.queue.approveLabel', { terminalId: session.terminalId }),
      // No `postedOn`: `branchToday` books the variance to the day the shop calls today, and this
      // device's clock is not that day.
      body: { sessionId: session.id, note: approvalNote.trim() },
    })) as { id: string }
    setApproveWriteId(write.id)
  }, [approvalNote, enqueue, session, t])

  const submitCount = useCallback(async () => {
    if (!session) return
    const minor = toMinorUnits(countInput)
    if (minor === null || minor < 0n) {
      setCountError(t('cashSession.countRequired'))
      return
    }
    setCountError(null)
    // Set before the write is even sent: this render is the one doing the counting, regardless of
    // how long the queue takes to reach the server.
    setRevealed(true)
    const write = (await enqueue({
      method: 'POST',
      path: '/cash-sessions/count',
      label: t('cashSession.queue.countLabel', { terminalId: session.terminalId }),
      body: { sessionId: session.id, countedMinor: minor.toString() },
    })) as { id: string }
    setCountWriteId(write.id)
  }, [session, countInput, enqueue, t])

  /**
   * Whether this reader can end a pending variance, which somebody has to be able to do.
   *
   * A variance of any size leaves the session `variance_pending` and the next shift cannot open a
   * drawer at that terminal (`SESSION_ALREADY_OPEN`), so every cash tender, every collection
   * carrying cash and every cash refund there fails on `CASH_SESSION_REQUIRED`. One rupee out and
   * the till took no cash again: the route existed and nothing in the product posted to it.
   */
  const canApproveThis =
    (canApproveVariance ?? false) &&
    session !== undefined &&
    session.status === 'variance_pending' &&
    // The server's rule, mirrored: the counter cannot approve their own drawer. Absent means
    // nobody has counted, which is not a state this panel renders in.
    session.countedByStaffId !== undefined &&
    session.countedByStaffId !== staffId

  const hasOpenSession = session !== undefined && session.status === 'open'
  const showOpenPanel = session === undefined || session.status === 'closed'
  const waitingOnSomeoneElse =
    session !== undefined &&
    (session.status === 'variance_pending' ||
      session.status === 'approved' ||
      session.status === 'counting')

  // This screen's own answer where it has one, the parent's otherwise.
  const shownResult = countedHere ?? countResult
  const variance = shownResult?.variance
  const varianceIsShort = variance !== undefined && variance.minor.startsWith('-')
  const absVariance =
    variance === undefined
      ? undefined
      : {
          minor: varianceIsShort ? variance.minor.slice(1) : variance.minor,
          currency: variance.currency,
        }

  return (
    <section className="lm-screen" aria-labelledby="cash-session-heading">
      <h1 id="cash-session-heading" className="lm-screen__heading">
        {t('nav.cash')}
      </h1>

      <p className="lm-queue-state" role="status">
        {connectionLine(t, snapshot)}
      </p>

      {session ? (
        <p className="lm-hint">
          {/* A terminal id is read out over a counter and an opening float is money, so both
              stay left to right whatever the page direction (`DESIGN-TOKENS.md` §6). */}
          {session.openingFloat === undefined
            ? sentence(t, 'cashSession.terminal', {
                terminalId: <Ltr>{session.terminalId}</Ltr>,
              })
            : sentence(t, 'cashSession.terminalOpenedWith', {
                terminalId: <Ltr>{session.terminalId}</Ltr>,
                openingFloat: <Ltr>{formatMoney(session.openingFloat)}</Ltr>,
              })}
          {' · '}
          <span className={session.status === 'open' ? 'lm-badge lm-badge--open' : 'lm-badge'}>
            {cashSessionStatusLabel(t, session.status)}
          </span>
        </p>
      ) : null}

      {showOpenPanel ? (
        <form
          className="lm-form"
          onSubmit={(e) => {
            e.preventDefault()
            void openDrawer()
          }}
        >
          <h2 className="lm-screen__subheading">{t('cashSession.openHeading')}</h2>

          <label className="lm-field">
            <span className="lm-field__label">{t('cashSession.openingFloatLabel')}</span>
            <input
              className="lm-input"
              inputMode="decimal"
              autoComplete="off"
              value={floatInput}
              onChange={(e) => setFloatInput(e.currentTarget.value)}
            />
          </label>

          <div className="lm-screen__actions">
            <button type="submit" className="lm-button lm-button--primary">
              {t('cashSession.openButton')}
            </button>
          </div>

          {floatError ? (
            <p className="lm-hint" role="status">
              {floatError}
            </p>
          ) : null}
          {writeLine(t, snapshot, openWriteId, t('queue.subject.openingFloat')) ? (
            <p className="lm-queue-state" role="status">
              {writeLine(t, snapshot, openWriteId, t('queue.subject.openingFloat'))}
            </p>
          ) : null}
        </form>
      ) : null}

      {waitingOnSomeoneElse && !canApproveThis ? (
        <p className="lm-hint" role="status">
          {t('cashSession.varianceWaiting')}
        </p>
      ) : null}

      {canApproveThis ? (
        <section className="lm-variance-approve" aria-labelledby="variance-approve-heading">
          <h2 id="variance-approve-heading" className="lm-screen__subheading">
            {t('cashSession.approveHeading')}
          </h2>
          {/* What approving does, before it is done. `ACC-001` posts the variance to
              `cash_variance`, so this is not a dismissal: the money is recognised as gone or found
              and the drawer is released for the next shift. */}
          <p className="lm-hint">{t('cashSession.approveMeans')}</p>
          <label className="lm-field">
            <span className="lm-field__label">{t('cashSession.approveNoteLabel')}</span>
            <input
              className="lm-input"
              autoComplete="off"
              value={approvalNote}
              onChange={(e) => {
                setApprovalNote(e.currentTarget.value)
              }}
            />
          </label>
          <p className="lm-hint">{t('cashSession.approveNoteMeans')}</p>
          <button
            type="button"
            className="lm-button lm-button--primary"
            disabled={approveWriteId !== null}
            onClick={() => {
              void approveVariance()
            }}
          >
            {t('cashSession.approve')}
          </button>
          {approvalError === null ? null : (
            <p className="lm-error" role="alert">
              {approvalError}
            </p>
          )}
          {writeLine(t, snapshot, approveWriteId, t('queue.subject.varianceApproval')) ? (
            <p className="lm-queue-state" role="status">
              {writeLine(t, snapshot, approveWriteId, t('queue.subject.varianceApproval'))}
            </p>
          ) : null}
        </section>
      ) : null}

      {hasOpenSession ? (
        <>
          <form
            className="lm-form"
            onSubmit={(e) => {
              e.preventDefault()
              void recordMovement()
            }}
          >
            <h2 className="lm-screen__subheading">{t('cashSession.movementHeading')}</h2>

            <fieldset className="lm-field">
              <legend className="lm-field__label">{t('cashSession.directionLegend')}</legend>
              <label>
                <input
                  type="radio"
                  name="cash-movement-direction"
                  value="in"
                  checked={direction === 'in'}
                  onChange={() => setDirection('in')}
                />{' '}
                {t('cashSession.directionIn')}
              </label>
              <label>
                <input
                  type="radio"
                  name="cash-movement-direction"
                  value="out"
                  checked={direction === 'out'}
                  onChange={() => setDirection('out')}
                />{' '}
                {t('cashSession.directionOut')}
              </label>
            </fieldset>

            <label className="lm-field">
              <span className="lm-field__label">{t('common.amount')}</span>
              <input
                className="lm-input"
                inputMode="decimal"
                autoComplete="off"
                value={amountInput}
                onChange={(e) => setAmountInput(e.currentTarget.value)}
              />
            </label>

            <label className="lm-field">
              <span className="lm-field__label">{t('common.reason')}</span>
              <input
                className="lm-input"
                autoComplete="off"
                value={reason}
                onChange={(e) => setReason(e.currentTarget.value)}
              />
            </label>

            <div className="lm-screen__actions">
              <button type="submit" className="lm-button lm-button--secondary">
                {t('cashSession.recordButton')}
              </button>
            </div>

            {movementError ? (
              <p className="lm-hint" role="status">
                {movementError}
              </p>
            ) : null}
            {writeLine(
              t,
              snapshot,
              movementWriteId,
              t(direction === 'out' ? 'queue.subject.payOut' : 'queue.subject.payIn'),
            ) ? (
              <p className="lm-queue-state" role="status">
                {writeLine(
                  t,
                  snapshot,
                  movementWriteId,
                  t(direction === 'out' ? 'queue.subject.payOut' : 'queue.subject.payIn'),
                )}
              </p>
            ) : null}

            <p className="lm-hint">
              {session === undefined ||
              session.movementCount === undefined ||
              session.movementCount === 0
                ? t('cashSession.movementsNone')
                : /* `count` stays a number, and that is a constraint rather than an oversight.
                     `translate()` selects the plural with `Intl.PluralRules` and throws if `count`
                     is not numeric, while `sentence()` substitutes a marker string for anything it
                     is given as an element, so a plural message cannot have its count isolated.
                     It does not need to be: the figure that reorders is a leading sign, and an
                     unsigned integer renders in place (measured, `later_todo.md` entry 3). */
                  t('cashSession.movementsRecorded', { count: session.movementCount })}
            </p>
          </form>

          <form
            className="lm-form"
            onSubmit={(e) => {
              e.preventDefault()
              void submitCount()
            }}
          >
            <h2 className="lm-screen__subheading">{t('cashSession.countAndClose')}</h2>

            {/*
             * No figure anywhere in this panel, on purpose. `Appendix C.7`'s blind count: the person
             * counting is never shown what the drawer is expected to hold, not even a running total
             * built from the float and the movements above, which this component has on hand and
             * deliberately does not add together.
             */}
            <p className="lm-hint">{t('cashSession.blindCountHint')}</p>

            <label className="lm-field">
              <span className="lm-field__label">{t('cashSession.countedLabel')}</span>
              <input
                className="lm-input"
                inputMode="decimal"
                autoComplete="off"
                value={countInput}
                onChange={(e) => setCountInput(e.currentTarget.value)}
              />
            </label>

            <div className="lm-screen__actions">
              <button type="submit" className="lm-button lm-button--primary">
                {t('cashSession.countAndClose')}
              </button>
            </div>

            {countError ? (
              <p className="lm-hint" role="status">
                {countError}
              </p>
            ) : null}
            {writeLine(t, snapshot, countWriteId, t('queue.subject.count')) ? (
              <p className="lm-queue-state" role="status">
                {writeLine(t, snapshot, countWriteId, t('queue.subject.count'))}
              </p>
            ) : null}
          </form>
        </>
      ) : null}

      {/*
       * The reconciliation. Gated on `revealed` as well as a result, so a stale prop from a previous
       * session cannot render here before this screen has submitted a count of its own.
       * There is no approve control here or anywhere in this file: `approveVariance` refuses the
       * person who counted, and a button that only works for someone else is not a control.
       */}
      {/*
       * Outside the open-session block, and the reason is the same one the reconciliation below is
       * outside it for. A count that the server accepts closes the drawer, so by the time this
       * message could be read the session is `closed` or `variance_pending` and the panel it was
       * first written inside has unmounted. It rendered for exactly one round trip and vanished,
       * leaving a till that had counted, seen nothing, and would reasonably conclude the drawer
       * balanced. Found by an audit of this change, not by the change itself.
       */}
      {resultError ? (
        <p className="lm-hint" role="status">
          {resultError}
        </p>
      ) : null}

      {revealed && shownResult ? (
        <div className="lm-paste-report" role="status">
          <h2 className="lm-screen__subheading">{t('cashSession.resultHeading')}</h2>
          <p className="lm-paste-report__line">
            {sentence(t, 'cashSession.resultCounted', {
              counted: <Ltr>{formatMoney(shownResult.counted)}</Ltr>,
              expected: <Ltr>{formatMoney(shownResult.expected)}</Ltr>,
            })}
          </p>
          <p className="lm-paste-report__line">
            {shownResult.balanced
              ? t('cashSession.resultBalanced')
              : sentence(
                  t,
                  varianceIsShort ? 'cashSession.resultShort' : 'cashSession.resultOver',
                  {
                    amount: <Ltr>{absVariance ? formatMoney(absVariance) : ''}</Ltr>,
                  },
                )}
          </p>
          <p className="lm-paste-report__line">
            {sentence(t, 'cashSession.zReportTotals', {
              openingFloat: <Ltr>{formatMoney(shownResult.zReport.openingFloat)}</Ltr>,
              cashTaken: <Ltr>{formatMoney(shownResult.zReport.cashTaken)}</Ltr>,
              payIns: <Ltr>{formatMoney(shownResult.zReport.payIns)}</Ltr>,
              payOuts: <Ltr>{formatMoney(shownResult.zReport.payOuts)}</Ltr>,
            })}
          </p>
          {shownResult.zReport.movements.length > 0 ? (
            <ul className="lm-cash-zreport">
              {shownResult.zReport.movements.map((m, index) => (
                // The Z-report line has no id of its own, and the list is a fixed snapshot of one
                // closed count rather than something that reorders, so the index is stable here.
                <li key={`${m.reason}-${m.amount.minor}-${index}`} className="lm-cash-zreport__row">
                  {sentence(t, 'cashSession.zReportMovement', {
                    // A pay-out is negative, and a leading minus is the character that moves.
                    amount: <Ltr>{formatMoney(m.amount)}</Ltr>,
                    reason: m.reason,
                  })}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}
